/**
 * Backend Tool Executor — Phase 3a
 *
 * Lets the Copilot actually DO tasks by calling service APIs with the USER'S
 * bearer + X-Tenant-ID (auto-attached by the existing axios apiClient).
 *
 * Two entry points:
 *   executeBackendTool(call)   — single capability call, Action-Guard gated
 *   executeChain(calls)        — ordered multi-step PROCESS with output binding
 *
 * Every write/destructive/outward call is classified by the Action Guard FIRST.
 * The actual HITL preview is driven by the caller (GrcCopilotSidebar) which owns
 * the UI; this module exposes `guardBackendTool()` so the caller can decide.
 * `executeBackendTool` itself always runs the API call once invoked — the gate
 * happens upstream of it (the planner→sidebar wiring). It still classifies and
 * attaches the guard result to the structured response for auditability.
 */

import { AxiosError } from 'axios'
import apiClient from '../api/client'
import { classifyAction, type ActionGuardResult, type BackendToolCall as GuardBackendToolCall } from './action-guard'

// ── Types ────────────────────────────────────────────────────────────────────

export interface BackendToolCall {
  /** Capability code from the registry, e.g. 'risk-service.post.risks' */
  capability_code: string
  /** HTTP verb: GET | POST | PUT | DELETE | PATCH */
  method: string
  /** Path relative to the api gateway, e.g. /risk-pro/risks */
  path: string
  /** Request body for write methods */
  body?: Record<string, unknown>
  /**
   * Optional binding expression: pull a value from a previous step's result and
   * either substitute it into the path (when the path contains the placeholder)
   * or merge it into the body. Format: '$<stepIndex1Based>.<dotpath>',
   * e.g. '$1.id' → result of step 1, field `id`.
   *
   * If the path contains '{id}' (or any '{...}') the bound value replaces it;
   * otherwise the bound value is injected into body under the last path segment
   * of the binding (e.g. '$1.id' → body.id).
   */
  bindFrom?: string
  /** Optional explicit side_effect override (otherwise inferred from method). */
  side_effect?: 'read' | 'write' | 'destructive' | 'outward'
  /** Human-readable description for the process preview card. */
  description?: string
}

export interface ToolResult {
  ok: boolean
  status: number
  data?: any
  capability_code: string
  /** Populated on failure. */
  error?: string
  /** The Action Guard classification recorded for this call. */
  guard?: ActionGuardResult
}

// ── Side-effect inference ───────────────────────────────────────────────────

function inferSideEffect(
  method: string,
  path: string,
): 'read' | 'write' | 'destructive' | 'outward' {
  const m = method.toUpperCase()
  if (m === 'DELETE' || /\/(delete|purge|destroy|archive|deactivate)/i.test(path)) {
    return 'destructive'
  }
  if (/\/(publish|submit|send|notify|dispatch|emit|broadcast|webhook)/i.test(path)) {
    return 'outward'
  }
  if (m === 'POST' || m === 'PUT' || m === 'PATCH') {
    return 'write'
  }
  return 'read'
}

/**
 * Classify a backend tool call via the shared Action Guard.
 * Maps the BackendToolCall shape onto the guard's expected `{ op, side_effect }`.
 */
export function guardBackendTool(call: BackendToolCall): ActionGuardResult {
  const side_effect = call.side_effect ?? inferSideEffect(call.method, call.path)
  const guardInput: GuardBackendToolCall = {
    op: 'backendTool',
    tool_name: call.capability_code,
    side_effect,
    form_id: call.capability_code,
    field_names: call.body ? Object.keys(call.body) : [],
  }
  return classifyAction(guardInput)
}

// ── Binding resolution ──────────────────────────────────────────────────────

/** Read a dot-path value out of an object, e.g. resolvePath(obj, 'data.id'). */
function resolveDotPath(obj: any, dotPath: string): unknown {
  return dotPath.split('.').reduce<any>((acc, key) => {
    if (acc == null) return undefined
    return acc[key]
  }, obj)
}

/**
 * Apply a `bindFrom` expression against the accumulated prior results.
 * Returns a NEW BackendToolCall with the bound value substituted into the path
 * (if the path has a `{placeholder}`) or merged into the body.
 *
 * bindFrom = '$1.id'  → take results[0].data.id (1-based step index)
 *            '$2.data.uuid' → results[1].data.data.uuid
 */
export function applyBinding(
  call: BackendToolCall,
  priorResults: ToolResult[],
): BackendToolCall {
  if (!call.bindFrom) return call

  const match = /^\$(\d+)\.(.+)$/.exec(call.bindFrom.trim())
  if (!match) return call

  const stepIndex = parseInt(match[1], 10) - 1 // 1-based → 0-based
  const dotPath = match[2]
  const prior = priorResults[stepIndex]
  if (!prior || !prior.ok) return call

  // Look in .data first, then the raw result, so '$1.id' finds result.data.id
  let bound = resolveDotPath(prior.data, dotPath)
  if (bound === undefined) {
    bound = resolveDotPath(prior, dotPath)
  }
  if (bound === undefined) return call

  const lastKey = dotPath.split('.').pop() ?? 'id'
  let newPath = call.path
  let newBody = call.body

  if (/\{[^}]+\}/.test(call.path)) {
    // Substitute the first {placeholder} in the path with the bound value
    newPath = call.path.replace(/\{[^}]+\}/, encodeURIComponent(String(bound)))
  } else if ((call.method ?? '').toUpperCase() === 'GET' || (call.method ?? '').toUpperCase() === 'DELETE') {
    // No body for GET/DELETE — append the bound id to the path
    newPath = `${call.path.replace(/\/$/, '')}/${encodeURIComponent(String(bound))}`
  } else {
    // Inject into the body
    newBody = { ...(call.body ?? {}), [lastKey]: bound }
  }

  return { ...call, path: newPath, body: newBody }
}

// ── Single tool execution ───────────────────────────────────────────────────

/**
 * Execute a single backend capability call through the shared axios apiClient
 * (bearer + X-Tenant-ID + correlation id auto-attached). Classifies the call
 * via the Action Guard and attaches the result for auditability.
 *
 * NOTE: this performs the network call unconditionally. HITL gating for
 * write/destructive/outward calls is enforced by the caller (the sidebar emits
 * a `copilot:preview` event and only invokes this on confirm). `guardBackendTool`
 * is exported so callers can decide whether to gate first.
 */
export async function executeBackendTool(
  call: BackendToolCall,
): Promise<ToolResult> {
  const guard = guardBackendTool(call)
  const method = (call.method ?? '').toUpperCase()

  try {
    const response = await apiClient.request({
      url: call.path,
      method: method as any,
      data: ['POST', 'PUT', 'PATCH'].includes(method) ? (call.body ?? {}) : undefined,
    })
    return {
      ok: true,
      status: response.status,
      data: response.data,
      capability_code: call.capability_code,
      guard,
    }
  } catch (err) {
    const axiosErr = err as AxiosError
    const status = axiosErr.response?.status ?? 0
    const data = axiosErr.response?.data
    return {
      ok: false,
      status,
      data,
      capability_code: call.capability_code,
      error:
        (data as { detail?: string; message?: string })?.detail ??
        (data as { message?: string })?.message ??
        axiosErr.message ??
        'Unknown error',
      guard,
    }
  }
}

// ── Chain execution ─────────────────────────────────────────────────────────

export interface ChainProgress {
  /** 0-based index of the step that just completed (or started). */
  stepIndex: number
  /** 'running' | 'done' | 'failed' */
  phase: 'running' | 'done' | 'failed'
  result?: ToolResult
}

/**
 * Execute an ordered chain of backend tool calls.
 * Each step may bind a value from a prior step's result via `bindFrom`.
 * On the first failed step the chain STOPS and returns the partial results
 * (failed step included as the last entry).
 *
 * @param calls    ordered steps
 * @param onProgress optional callback invoked before+after each step for live UI
 */
export async function executeChain(
  calls: BackendToolCall[],
  onProgress?: (p: ChainProgress) => void,
): Promise<ToolResult[]> {
  const results: ToolResult[] = []

  for (let i = 0; i < calls.length; i++) {
    onProgress?.({ stepIndex: i, phase: 'running' })

    const bound = applyBinding(calls[i], results)
    const result = await executeBackendTool(bound)
    results.push(result)

    if (!result.ok) {
      onProgress?.({ stepIndex: i, phase: 'failed', result })
      // Stop the chain on the first failure.
      break
    }

    onProgress?.({ stepIndex: i, phase: 'done', result })
  }

  return results
}

// ── Window bridge augmentation (Phase 3a) ────────────────────────────────────
//
// Expose the executor on window.sgrcCopilot for headed-Chromium verification
// and for the planner-driven sidebar. The action-bridge owns the base object;
// here we extend it idempotently if it already exists.

declare global {
  interface Window {
    sgrcCopilotTools?: {
      executeBackendTool: (call: BackendToolCall) => Promise<ToolResult>
      executeChain: (
        calls: BackendToolCall[],
        onProgress?: (p: ChainProgress) => void,
      ) => Promise<ToolResult[]>
      guardBackendTool: (call: BackendToolCall) => ActionGuardResult
      applyBinding: (call: BackendToolCall, prior: ToolResult[]) => BackendToolCall
    }
  }
}

export function mountToolExecutorBridge() {
  if (typeof window === 'undefined') return
  window.sgrcCopilotTools = {
    executeBackendTool,
    executeChain,
    guardBackendTool,
    applyBinding,
  }
}
